"use client";

import { useEffect, useState } from "react";
import BlurredPlaceholder from "@/app/(components)/generics/BlurredPlaceholder/BlurredPlaceholder";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import { ScriptItemData } from "@/app/types/messages-content/ScriptData";
import { UScriptSuggestions } from "@/app/utils/UScriptSuggestions";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { buildScriptPlaceholderRows } from "../../Scripts.logic";
import styles from "./ScriptCallItems.module.css";

type Props = {
  blockType: string;
  title: string;
  isPlaceholder: boolean;
  items: ScriptItemData[];
  responsePlaceholder: string;
  onOpenQuotes: (insightId: number, label: string) => void;
  onSaveResponse?: (label: string, response: string) => void;
};

export default function ScriptCallItems(props: Props) {
  const {
    blockType,
    title,
    isPlaceholder,
    items,
    responsePlaceholder,
    onOpenQuotes,
    onSaveResponse,
  } = props;

  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    setIsCollapsed(UScriptSuggestions.isCollapsed(blockType));
  }, [blockType]);

  if (isPlaceholder) {
    return (
      <div className={styles.container}>
        <div className={styles.headStatic}>
          <span className={styles.title}>{title}</span>
        </div>
        <BlurredPlaceholder rows={buildScriptPlaceholderRows(3)} className={styles.placeholder} />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.headStatic}>
          <span className={styles.title}>{title}</span>
          <span className={styles.count}>{items.length}</span>
        </div>
      </div>
    );
  }

  const toggle = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    UScriptSuggestions.setCollapsed(blockType, next);
  };

  return (
    <div className={styles.container}>
      <button type="button" className={styles.head} onClick={toggle} aria-expanded={!isCollapsed}>
        <span className={UStyles.classNames(styles.arrow, !isCollapsed && styles.arrowOpen)}>
          <IconArrowDown />
        </span>
        <span className={styles.title}>{title}</span>
        <span className={styles.count}>{items.length}</span>
      </button>

      {!isCollapsed && (
        <ul className={styles.list}>
          {items.map((item) => (
            <li key={item.id} className={styles.item}>
              <button
                type="button"
                className={styles.label}
                onClick={() =>
                  item.sourceInsightId && onOpenQuotes(item.sourceInsightId, item.label)
                }
              >
                {item.label}
              </button>
              <EditableText
                value={item.response ?? ""}
                onSave={(value) => onSaveResponse?.(item.label, value)}
                placeholder={responsePlaceholder}
                multiline={true}
                maxLength={Limits.MAX_SCRIPT_ITEM_RESPONSE}
                className={styles.response}
                textClassName={styles.responseText}
                inputClassName={styles.responseText}
                disabled={!onSaveResponse}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
